import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@/hooks/useTranslation';
import { Mail, CheckCircle2 } from 'lucide-react';
import { AuthLayout, FIELD_WITH_ICON_CLASS } from '@/components/landing/page-kit';

const forgotPasswordSchema = z.object({
  email: z.string().email('Email invalide'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(data.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      setEmailSent(true);
      toast({
        title: t('forgotPassword.toast.success'),
        description: t('forgotPassword.toast.successDesc'),
      });
    } catch (error) {
      toast({
        title: t('forgotPassword.toast.error'),
        description: error instanceof Error ? error.message : t('forgotPassword.toast.errorDesc'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      back={{ to: '/login', label: t('forgotPassword.backToLogin') }}
      title={t('forgotPassword.title')}
      subtitle={t('forgotPassword.subtitle')}
      aside={
        <p>
          {t('forgotPassword.help')}{' '}
          <Link
            to="/login"
            className="text-brand-700 transition-opacity hover:opacity-80 dark:text-brand-400"
          >
            {t('forgotPassword.login')}
          </Link>
        </p>
      }
    >
      {emailSent ? (
        <div className="space-y-6 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-primary/30 bg-primary/10">
            <CheckCircle2 className="h-6 w-6 text-primary" />
          </span>
          <div className="space-y-2">
            <h2 className="text-lg font-medium">{t('forgotPassword.success.title')}</h2>
            <p className="text-sm text-muted-foreground">
              {t('forgotPassword.success.message')}{' '}
              <span className="font-medium text-brand-700 dark:text-brand-400">
                {getValues('email')}
              </span>
            </p>
            <p className="text-sm text-muted-foreground">
              {t('forgotPassword.success.instruction')}
            </p>
          </div>
          <Button asChild className="h-11 w-full shadow-lg shadow-brand-600/20">
            <Link to="/login">{t('forgotPassword.backToLogin')}</Link>
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">
              {t('forgotPassword.email')}
            </Label>
            <div className="group relative">
              <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground transition-colors group-focus-within:text-primary" />
              <Input
                id="email"
                type="email"
                placeholder={t('forgotPassword.emailPlaceholder')}
                {...register('email')}
                disabled={isLoading}
                className={FIELD_WITH_ICON_CLASS}
              />
            </div>
            {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="h-11 w-full shadow-lg shadow-brand-600/20 transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/25 border-t-white" />
                {t('forgotPassword.loading')}
              </span>
            ) : (
              t('forgotPassword.submit')
            )}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
